import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchUsers,
  createUser,
  deleteUser,
  updateUser,
  generateNewKey,
} from "../services/api";
import CreateUserModal from "./modals/CreateUserModal";
import EditUserModal from "./modals/EditUserModal";
import DeleteConfirmModal from "./modals/DeleteConfirmModal";
import GenerateKeyModal from "./modals/GenerateKeyModal";
import "./UserManagement.css";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * User Management tab — lists all API users in a table with pagination
 * and provides actions for creating, editing, deleting, and regenerating keys.
 */
function UserManagement() {
  const { token, isTokenValid } = useAuth();

  // Data state
  const [users, setUsers] = useState([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_count: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [generatingKeyUser, setGeneratingKeyUser] = useState(null);

  // Action loading state
  const [actionLoading, setActionLoading] = useState(false);

  // ── Fetch users ─────────────────────────────────────────────────────────

  const loadUsers = useCallback(
    async (page = 1, size = pageSize) => {
      if (!isTokenValid()) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchUsers(token, page, size);
        setUsers(data.users || []);
        setPagination(data.pagination || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [token, isTokenValid, pageSize]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ── Create user ─────────────────────────────────────────────────────────

  const handleCreate = async ({ name, email }) => {
    if (!isTokenValid()) return;
    setActionLoading(true);
    try {
      await createUser(token, { name, email });
      setShowCreateModal(false);
      loadUsers(pagination.page);
    } catch (err) {
      throw err; // Let the modal display the error
    } finally {
      setActionLoading(false);
    }
  };

  // ── Update user ─────────────────────────────────────────────────────────

  const handleUpdate = async ({ id, name, email }) => {
    if (!isTokenValid()) return;
    setActionLoading(true);
    try {
      await updateUser(token, { id, name, email });
      setEditingUser(null);
      loadUsers(pagination.page);
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete user ─────────────────────────────────────────────────────────

  const handleDelete = async (userId) => {
    if (!isTokenValid()) return;
    setActionLoading(true);
    try {
      await deleteUser(token, userId);
      setDeletingUser(null);
      loadUsers(pagination.page);
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // ── Generate new key ────────────────────────────────────────────────────

  const handleGenerateKey = async (userId, userName) => {
    if (!isTokenValid()) return;
    setActionLoading(true);
    try {
      await generateNewKey(token, userId);
      setGeneratingKeyUser(null);
      loadUsers(pagination.page);
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // ── Pagination ──────────────────────────────────────────────────────────

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      loadUsers(page);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    loadUsers(1, newSize);
  };

  /**
   * Build an array of page numbers to display, with ellipsis gaps.
   * Always shows first, last, and a window around the current page.
   */
  const getPageNumbers = () => {
    const total = pagination.total_pages;
    const current = pagination.page;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = new Set([1, total]);
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.add(i);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        result.push("...");
      }
      result.push(sorted[i]);
    }
    return result;
  };

  // ── Copy to clipboard ─────────────────────────────────────────────────

  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 1500);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateKey = (key) => {
    if (!key) return "—";
    if (key.length > 60) {
      return key.substring(0, 60) + "...";
    }
    return key;
  };

  return (
    <div className="user-mgmt">
      <div className="user-mgmt-header">
        <div>
          <h1 className="user-mgmt-title">User Management</h1>
          <p className="user-mgmt-subtitle">
            Manage API users who access the Insuraviews endpoints.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <svg
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create User
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="user-mgmt-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {/* Table */}
      <div className="user-mgmt-table-wrapper">
        <table className="user-mgmt-table">
          <thead>
            <tr>
              <th>Id</th>
              <th>Name</th>
              <th>Email</th>
              <th>Refresh Token</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Last Updated</th>
              <th>Modified By</th>
              <th className="user-mgmt-actions-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="9" className="user-mgmt-loading">
                  Loading...
                </td>
              </tr>
            )}

            {!loading && users.length === 0 && (
              <tr>
                <td colSpan="9" className="user-mgmt-empty">
                  No users found. Create one to get started.
                </td>
              </tr>
            )}

            {!loading &&
              users.map((u) => (
                <tr key={u.id}>
                  <td className="user-mgmt-id user-mgmt-copyable-cell">
                    <span>{u.id?.substring(0, 8)}...</span>
                    <button
                      className="user-mgmt-copy-btn"
                      title="Copy full ID"
                      onClick={() => u.id && copyToClipboard(u.id, `id-${u.id}`)}
                    >
                      {copiedField === `id-${u.id}` ? (
                        <svg width="14" height="14" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      )}
                    </button>
                  </td>
                  <td>{u.name || "—"}</td>
                  <td>{u.email || "—"}</td>
                  <td className="user-mgmt-key user-mgmt-copyable-cell">
                    <code>{truncateKey(u.refresh_token)}</code>
                    <button
                      className="user-mgmt-copy-btn"
                      title="Copy full refresh token"
                      onClick={() => u.refresh_token && copyToClipboard(u.refresh_token, `key-${u.id}`)}
                    >
                      {copiedField === `key-${u.id}` ? (
                        <svg width="14" height="14" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                      ) : (
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                      )}
                    </button>
                  </td>
                  <td>{u.created_by || "—"}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>{formatDate(u.last_updated_at)}</td>
                  <td>{u.last_modified_by || "—"}</td>
                  <td className="user-mgmt-actions">
                    {/* Generate New Key */}
                    <button
                      className="btn btn-sm btn-outline"
                      title="Generate New Key"
                      onClick={() => setGeneratingKeyUser(u)}
                    >
                      <svg
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                      </svg>
                      <span className="btn-label-desktop">Key</span>
                    </button>

                    {/* Edit */}
                    <button
                      className="btn btn-sm btn-outline"
                      title="Edit User"
                      onClick={() => setEditingUser(u)}
                    >
                      <svg
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>

                    {/* Delete */}
                    <button
                      className="btn btn-sm btn-danger-outline"
                      title="Delete User"
                      onClick={() => setDeletingUser(u)}
                    >
                      <svg
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total_count > 0 && (
        <div className="user-mgmt-pagination">
          <div className="user-mgmt-pagination-left">
            <label className="user-mgmt-pagesize-label">
              Rows per page:
              <select
                className="user-mgmt-pagesize-select"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <span className="user-mgmt-pagination-info">
              {(pagination.page - 1) * pagination.page_size + 1}–
              {Math.min(pagination.page * pagination.page_size, pagination.total_count)}
              {" "}of {pagination.total_count}
            </span>
          </div>

          {pagination.total_pages > 1 && (
            <div className="user-mgmt-pagination-buttons">
              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(1)}
                title="First page"
              >
                &laquo;
              </button>
              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
                title="Previous page"
              >
                &lsaquo;
              </button>

              {getPageNumbers().map((p, idx) =>
                p === "..." ? (
                  <span key={`ellipsis-${idx}`} className="user-mgmt-pagination-ellipsis">
                    &hellip;
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`btn btn-sm ${p === pagination.page ? "btn-primary" : "btn-outline"}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => goToPage(pagination.page + 1)}
                title="Next page"
              >
                &rsaquo;
              </button>
              <button
                className="btn btn-sm btn-outline"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => goToPage(pagination.total_pages)}
                title="Last page"
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          loading={actionLoading}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={handleUpdate}
          loading={actionLoading}
        />
      )}

      {deletingUser && (
        <DeleteConfirmModal
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={() => handleDelete(deletingUser.id)}
          loading={actionLoading}
        />
      )}

      {generatingKeyUser && (
        <GenerateKeyModal
          user={generatingKeyUser}
          onClose={() => setGeneratingKeyUser(null)}
          onConfirm={() =>
            handleGenerateKey(generatingKeyUser.id, generatingKeyUser.name)
          }
          loading={actionLoading}
        />
      )}
    </div>
  );
}

export default UserManagement;
